/**
 * SkillSnap Helper (browser extension) bridge.
 *
 * Companion extension at chrome_extension/ in this repo (Chrome/Edge MV3, side panel).
 * When the user clicks "Apply" on a ready job, we push the per-job *tailored*
 * resume + cover letter into the extension so the side panel is pre-loaded
 * with exactly the right data for filling out the application form.
 *
 * Hard rules for this module:
 *   - The extension is 100% optional. If it isn't installed, this MUST be a
 *     silent no-op — no exceptions, no console errors, no warnings.
 *   - No visible UI: do not toast, do not branch the webapp on whether the
 *     extension is present.
 *   - Synchronous from the caller's perspective: fire-and-forget.
 */
import yaml from 'js-yaml';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SKILLSNAP_HELPER_EXTENSION_ID } from '../constants';
import { apiConfig } from '../config/amplify';
import { api } from './api';
import type { ApplicationAnswer, UserJob } from '../types';

// Minimal typing for the Chromium runtime API on the window. We don't depend
// on @types/chrome to keep webapp's type surface small; we just feature-detect.
type ChromeRuntime = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    responseCallback?: (response: unknown) => void,
  ) => void;
  lastError?: { message?: string } | undefined;
};
type MaybeChrome = { runtime?: ChromeRuntime } | undefined;

/**
 * Strip a leading 3-digit `match_score` prefix like "823 " from a string.
 *
 * Mirrors `_strip_score_prefix` in
 * `infrastructure/lambdas/generation/final_files.py` — these scores are the
 * embedding-match values the AI uses while building the tailored sections,
 * and the generation pipeline strips them before rendering. The list endpoint
 * preserves them in the per-job data* fields, so we need to strip on the
 * extension push path too.
 *
 * Only strips when there's *exactly* "DDD " at the start (3 digits + space).
 * Real data values like "1998 - 2002" or "20+ years" don't match.
 */
function stripScorePrefix(text: unknown): string {
  if (typeof text !== 'string') return text as unknown as string;
  if (text.length >= 4 && /^\d{3} /.test(text)) return text.slice(4);
  return text;
}

/**
 * Recursively strip score prefixes from a parsed section. Mirrors the Python
 * `strip_scores_from_data`: only touches plain strings, `text`/`title` fields
 * of dicts, and bullets nested under `roles` (the experience hierarchy).
 * Doesn't touch tags, urls, years, dates, locations, etc.
 */
function stripScoresFromData(data: unknown): unknown {
  if (data == null) return data;
  if (typeof data === 'string') return stripScorePrefix(data);
  if (Array.isArray(data)) {
    return data.map((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const obj: Record<string, unknown> = { ...(item as Record<string, unknown>) };
        if (typeof obj.text === 'string') obj.text = stripScorePrefix(obj.text);
        if (typeof obj.title === 'string') obj.title = stripScorePrefix(obj.title);
        // Experience: companies -> roles -> bullets
        if (Array.isArray(obj.roles)) {
          obj.roles = (obj.roles as unknown[]).map((role) => {
            if (!role || typeof role !== 'object') return role;
            const r: Record<string, unknown> = { ...(role as Record<string, unknown>) };
            if (Array.isArray(r.bullets)) {
              r.bullets = (r.bullets as unknown[]).map((b) => {
                if (b && typeof b === 'object' && !Array.isArray(b)) {
                  const bb: Record<string, unknown> = { ...(b as Record<string, unknown>) };
                  if (typeof bb.text === 'string') bb.text = stripScorePrefix(bb.text);
                  return bb;
                }
                if (typeof b === 'string') return stripScorePrefix(b);
                return b;
              });
            }
            return r;
          });
        }
        return obj;
      }
      if (typeof item === 'string') return stripScorePrefix(item);
      return item;
    });
  }
  return data;
}

/** Parse a YAML data* string, normalizing the {section: value} wrapper. */
function parseSection<T = unknown>(raw: string | undefined, ...keys: string[]): T | undefined {
  if (!raw || typeof raw !== 'string' || !raw.trim()) return undefined;
  let parsed: unknown;
  try {
    parsed = yaml.load(raw);
  } catch {
    // YAML can come back malformed from the LLM occasionally; fall back to
    // treating the string itself as the value for scalar sections.
    return stripScorePrefix(raw.trim()) as unknown as T;
  }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const obj = parsed as Record<string, unknown>;
    for (const k of keys) {
      if (k in obj) return stripScoresFromData(obj[k]) as T;
    }
  }
  return stripScoresFromData(parsed) as T;
}

/**
 * Build a ResumeJSON-shaped object from the per-job tailored data* fields.
 * Returns undefined if there's effectively nothing to push.
 */
function buildPayloadFromJob(job: UserJob): { resume: Record<string, unknown>; coverletter?: string } | undefined {
  const resume: Record<string, unknown> = {};

  if (job.dataname && job.dataname.trim()) resume.name = stripScorePrefix(job.dataname.trim());

  const contact = parseSection(job.datacontact, 'contact', 'contacts');
  if (contact) resume.contact = contact;

  const summary = parseSection<string>(job.datasummary, 'summary');
  if (summary) resume.summary = summary;

  const skills = parseSection(job.dataskills, 'skills');
  if (skills) resume.skills = skills;

  const highlights = parseSection(job.datahighlights, 'highlights');
  if (highlights) resume.highlights = highlights;

  const experience = parseSection(job.dataexperience, 'experience');
  if (experience) resume.experience = experience;

  const education = parseSection(job.dataeducation, 'education');
  if (education) resume.education = education;

  const awards = parseSection(job.dataawards, 'awards');
  if (awards) resume.awards = awards;

  const presentations = parseSection(job.datapresentations, 'presentations', 'keynotes');
  if (presentations) resume.presentations = presentations;

  const publications = parseSection(job.datapublications, 'publications');
  if (publications) resume.publications = publications;

  const portfolios = parseSection(job.dataportfolios, 'portfolios');
  if (portfolios) resume.portfolios = portfolios;

  const coverletterRaw = (job.datacoverletter || '').trim();
  const coverletter = coverletterRaw ? stripScorePrefix(coverletterRaw) : undefined;
  if (coverletter) resume.coverletter = coverletter;

  // Nothing useful to send — bail.
  if (Object.keys(resume).length === 0) return undefined;

  return { resume, coverletter };
}

/**
 * Push the tailored resume for a job to the SkillSnap Extension, if installed.
 * Silent on every failure mode — never throws, never logs.
 *
 * The `job` passed in from a JobCard is the *slimmed* shape (the /jobs list
 * endpoint collapses every data{section} YAML field to the marker string "1"
 * to stay under the 6 MB Lambda response limit — see
 * `infrastructure/lambdas/job/list.py:_slim_user_job`). We always fetch the
 * full job via `/jobs/{jobid}` before building the payload so the extension
 * gets real tailored content.
 */
export function pushJobToHelper(job: UserJob | undefined | null): void {
  try {
    if (!job || !job.jobid) return;

    // Feature-detect the Chromium messaging API. On Firefox/Safari, or in any
    // Chromium with no compatible extension installed, this is a clean no-op.
    const chrome = (globalThis as unknown as { chrome?: MaybeChrome }).chrome;
    const runtime = chrome && chrome.runtime;
    if (!runtime || typeof runtime.sendMessage !== 'function') return;

    // Async fetch + push. We do this fire-and-forget so the Apply click path
    // never blocks on it. The popups open synchronously; this message arrives
    // a few hundred ms later, which is fine — the user is switching tabs.
    void (async () => {
      try {
        const fullJob = await api.getJob(job.jobid);
        const built = buildPayloadFromJob(fullJob);
        if (!built) return;

        // Contact is a Manual section, so the per-job data* snapshot freezes
        // it at generation time — a contact item added to the resume after
        // that never reaches the extension. Overlay the live resume's contact
        // (minus explicitly excluded items, mirroring the generation-time
        // include pre-filter). On any failure the snapshot is still pushed.
        try {
          if (fullJob.resumeid) {
            const liveResume = await api.getResume(fullJob.resumeid);
            const liveContact = (liveResume?.resumejson?.contact ?? []).filter(
              (c) => !(c && typeof c === 'object' && c.include === '0'),
            );
            if (liveContact.length) built.resume.contact = liveContact;
            const liveName = (liveResume?.resumejson?.name ?? '').trim();
            if (liveName) built.resume.name = liveName;
            // Demographics live only on the resume (never generated into the
            // per-job data* snapshot), so overlay them here — they drive the
            // extension's Demographic section + phone-country-code derivation.
            const liveDemographics = liveResume?.resumejson?.demographics;
            if (liveDemographics && typeof liveDemographics === 'object') {
              built.resume.demographics = liveDemographics;
            }
          }
        } catch {
          // Live-resume fetch failed — the generation-time snapshot stands.
        }

        const label = [fullJob.jobcompany, fullJob.jobtitle].filter(Boolean).join(' — ') || undefined;
        const message = {
          type: 'skillsnap:loadResume',
          data: built.resume,
          label,
          job: {
            jobid: fullJob.jobid,
            jobcompany: fullJob.jobcompany,
            jobtitle: fullJob.jobtitle,
            joburl: fullJob.joburl,
          },
        };

        runtime.sendMessage(SKILLSNAP_HELPER_EXTENSION_ID, message, () => {
          void (runtime.lastError && runtime.lastError.message);
        });
      } catch {
        // Fetch / parse / send failed — silent.
      }
    })();
  } catch {
    // Defense in depth: any unexpected throw is swallowed so the calling
    // Apply-button click path is never disrupted.
  }
}

/**
 * Push the user's application Q&A answers to the SkillSnap Extension's
 * "Answers" tab. Filtered to answers that are either linked to this specific
 * job OR not linked to any job (general-purpose answers like salary or work
 * authorization).
 *
 * Async, fire-and-forget. Silent on every failure mode — never throws, never
 * logs, never blocks the calling Apply-button click path.
 */
export function pushAnswersToHelper(job: UserJob | undefined | null): void {
  try {
    if (!job || !job.jobid) return;

    const chrome = (globalThis as unknown as { chrome?: MaybeChrome }).chrome;
    const runtime = chrome && chrome.runtime;
    if (!runtime || typeof runtime.sendMessage !== 'function') return;

    // Fetch answers asynchronously; the user has already clicked Apply and
    // popups are opening — a few hundred ms of latency is fine here.
    void (async () => {
      try {
        const resp = await api.listApplicationAnswers();
        const all: ApplicationAnswer[] = Array.isArray(resp?.answers) ? resp.answers : [];

        // Include every answer that actually has answer text. Filter out
        // placeholder rows ("New question" with no body) — they're not
        // fillable data. *Don't* filter by linked_jobids: answers linked to
        // a different job are still reusable text the user may want here.
        const usable = all
          .filter((a) => a && typeof a.answer === 'string' && a.answer.trim() !== '')
          .map((a) => {
            const links = Array.isArray(a.linked_jobids) ? a.linked_jobids : [];
            return {
              question: (a.question || '').trim(),
              answer: a.answer.trim(),
              linked_jobids: links,
              linked_to_this_job: links.includes(job.jobid),
            };
          });

        // Sort relevance: linked-to-this-job first, then general (no links),
        // then linked-to-other-jobs. Stable within each group.
        usable.sort((a, b) => {
          const rank = (x: typeof a) =>
            x.linked_to_this_job ? 0 : x.linked_jobids.length === 0 ? 1 : 2;
          return rank(a) - rank(b);
        });

        if (!usable.length) return;
        runtime.sendMessage(
          SKILLSNAP_HELPER_EXTENSION_ID,
          { type: 'skillsnap:loadAnswers', data: usable, jobid: job.jobid },
          () => { void (runtime.lastError && runtime.lastError.message); },
        );
      } catch {
        // Network / auth / parse failure — silent.
      }
    })();
  } catch {
    // ignore
  }
}

/**
 * Push the user's API base + a short-lived Cognito idToken to the SkillSnap
 * Extension so its "Queue this Job" button can call `POST /jobs/url` as this
 * user.
 *
 * Security posture (deliberately conservative): the extension gets EXACTLY the
 * same bearer the webapp already uses for its own API calls — the idToken
 * (~1 h lifetime), nothing more. No new long-lived secret is minted and no new
 * backend auth surface is added; the ingest route stays behind the same Cognito
 * authorizer. The extension stores it in chrome.storage.local (the same trust
 * level Amplify uses for its own token storage) and discards it on expiry.
 *
 * We re-push while the app tab is open (mount + visibility + timer, wired in
 * App.tsx) so the token the extension holds stays fresh; if it does go stale the
 * extension asks the user to reopen skillsnap.me rather than failing silently.
 *
 * Async, fire-and-forget. Silent on every failure mode — never throws, never
 * logs. A clean no-op when the extension isn't installed or the user is signed
 * out.
 */
export function pushAuthToHelper(): void {
  try {
    const chrome = (globalThis as unknown as { chrome?: MaybeChrome }).chrome;
    const runtime = chrome && chrome.runtime;
    if (!runtime || typeof runtime.sendMessage !== 'function') return;

    void (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        const token = idToken?.toString();
        if (!token) return; // signed out — nothing to share

        // idToken `exp` is seconds since epoch; hand the extension the ms value
        // so it can pre-empt a call it knows would 401 and prompt a refresh.
        const rawExp = idToken?.payload?.exp;
        const exp = typeof rawExp === 'number' ? rawExp * 1000 : undefined;

        runtime.sendMessage(
          SKILLSNAP_HELPER_EXTENSION_ID,
          { type: 'skillsnap:loadAuth', apiBase: apiConfig.baseUrl, token, exp },
          () => { void (runtime.lastError && runtime.lastError.message); },
        );
      } catch {
        // Not signed in / Amplify not ready / send failed — silent.
      }
    })();
  } catch {
    // Defense in depth: never disrupt the caller.
  }
}
